import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { toast } from 'sonner'
import { Loader2, Plus } from 'lucide-react'
import { Card } from '@orisini/ui/components/card'
import { Avatar } from '../components/Avatar'
import { StatusBadge } from '../components/StatusBadge'
import { accountsApi, type Workspace } from '../lib/accounts'

/**
 * /projects — semua project, kartu terakhir "New project".
 *
 * Setiap project adalah ruang produksi kontennya sendiri. Akun media sosial
 * hanya keterangan di bawah namanya, dan boleh tidak ada.
 */
export default function ProjectsPage() {
  const [projects, setProjects] = useState<Workspace[] | null>(null)

  useEffect(() => {
    accountsApi
      .workspaces()
      .then(setProjects)
      .catch((err) => {
        setProjects([])
        toast.error(err instanceof Error ? err.message : "Couldn't load your projects")
      })
  }, [])

  return (
    <div className="space-y-6">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight">Projects</h1>
        <p className="text-sm text-muted-foreground">
          {projects === null
            ? 'Each project has its own topics, brand and content formats.'
            : `${projects.length} ${projects.length === 1 ? 'project' : 'projects'}`}
        </p>
      </header>

      {projects === null ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" /> Loading…
        </div>
      ) : (
        <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {projects.map((w) => (
            <li key={w.id}>
              <ProjectCard project={w} />
            </li>
          ))}
          <li>
            <Link
              to="/projects/new"
              className="flex h-full min-h-36 flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-border p-5 text-center text-muted-foreground transition-colors hover:border-foreground/40 hover:text-foreground"
            >
              <Plus className="size-5" />
              <span className="text-sm font-medium">New project</span>
              <span className="text-xs">No social account needed</span>
            </Link>
          </li>
        </ul>
      )}
    </div>
  )
}

function ProjectCard({ project: w }: { project: Workspace }) {
  const handle = w.account?.username ? `@${w.account.username}` : null
  return (
    <Link to={`/projects/${w.id}`} className="block h-full">
      <Card className="flex h-full min-h-36 flex-col gap-3 p-5 transition-colors hover:border-foreground/30">
        <div className="flex items-start gap-3">
          <Avatar url={w.account?.avatarUrl ?? null} name={w.name} />
          <div className="min-w-0 flex-1">
            <p className="truncate font-medium">{w.name}</p>
            <p className="truncate text-xs text-muted-foreground">
              {w.productCode ? [w.platform, handle].filter(Boolean).join(' · ') : 'Download only'}
            </p>
          </div>
        </div>
        <div className="mt-auto space-y-1.5">
          <StatusBadge status={w.status} />
          {w.statusReason && <p className="text-xs text-muted-foreground">{w.statusReason}</p>}
        </div>
      </Card>
    </Link>
  )
}
